import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { CartItemsComponent } from './component-cart-items.js';

// Régua de brindes 99K: cordão R$149,90, necessaire R$199,90, pulseira R$249,90.
const gifts = new Map([
  [48965460426870, 14990],
  [48965460131958, 19990],
  [48965460459638, 24990],
]);

class GiftSelector99K extends HTMLElement {
  static busy = false;
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    scheduleGiftEligibility99K();
  }
  disconnectedCallback() { this.removeEventListener('click', this.onClick); }

  async getCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Não foi possível conferir o carrinho. Tente novamente.');
    return response.json();
  }

  async post(url, payload) {
    const response = await fetch(url, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok || result.errors) throw new Error(result.description || result.message || 'Não foi possível atualizar o brinde.');
    return result;
  }

  // Regra 99K: vale o total do carrinho já com os descontos (cupom incluído), sem o frete
  // e sem o valor do próprio brinde.
  eligibleTotal(cart) {
    const giftsValue = cart.items.reduce((total, item) => (
      gifts.has(Number(item.variant_id)) ? total + item.final_line_price : total
    ), 0);
    return Math.max(0, cart.total_price - giftsValue);
  }

  publishCart(cart, sections) {
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'cart', lines: [],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections, items: cart.items, itemCount: cart.item_count, source: '99k-gift-selector', didError: false },
      }),
    }));
  }

  async refresh(sections) {
    const url = new URL(Theme.routes.cart_url, window.location.origin);
    url.searchParams.set('sections', sections.join(','));
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Recarregue a página para conferir o carrinho.');
    const rendered = await response.json();
    const cart = await this.getCart();
    this.publishCart(cart, rendered);
  }

  onClick = async (event) => {
    const button = event.target.closest('button[data-gift-variant]');
    if (!button || !this.contains(button) || button.disabled || GiftSelector99K.busy) return;
    const variantId = Number(button.dataset.giftVariant);
    const threshold = gifts.get(variantId);
    if (!threshold) return;
    const originalStates = Array.from(this.querySelectorAll('button[data-gift-variant]')).map((item) => ({ button: item, disabled: item.disabled, text: item.textContent }));
    GiftSelector99K.busy = true;
    originalStates.forEach(({ button: item }) => { item.disabled = true; });
    button.textContent = 'Aguarde…';
    const message = this.querySelector('[data-gift-message]');
    if (message) message.textContent = '';
    const sectionIds = new Set([this.dataset.sectionId, 'cart-drawer-section']);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => sectionIds.add(item.dataset.sectionId));
    const sections = Array.from(sectionIds).filter(Boolean).slice(0, 5);
    const rendering = { sections, sections_url: window.location.pathname };
    let previousGift = null;
    let removedPrevious = false;
    let cartChanged = false;

    try {
      await Promise.all(CartItemsComponent.pendingQuantityUpdates);
      const cartBefore = await this.getCart();
      if (this.eligibleTotal(cartBefore) < threshold) throw new Error('O carrinho ainda não atingiu a meta desse brinde.');
      const existing = cartBefore.items.filter((item) => gifts.has(Number(item.variant_id)));
      if (existing.length > 1 || existing.some((item) => item.quantity !== 1 || item.final_line_price !== 0)) {
        throw new Error('Confira os itens de brinde no carrinho: a troca automática exige apenas 1 brinde gratuito, com quantidade 1.');
      }
      previousGift = existing[0] || null;
      if (previousGift && Number(previousGift.variant_id) === variantId) throw new Error('Esse brinde já está selecionado.');

      if (previousGift) {
        button.textContent = 'Trocando…';
        await this.post(Theme.routes.cart_change_url, { id: previousGift.key, quantity: 0, ...rendering });
        removedPrevious = true;
        cartChanged = true;
      } else {
        button.textContent = 'Adicionando…';
      }
      // Confirm the latest cart state before adding, including changes from another tab.
      const cartReady = await this.getCart();
      if (cartReady.items.some((item) => gifts.has(Number(item.variant_id)))) throw new Error('Outro brinde foi encontrado. Confira o carrinho antes de tentar novamente.');
      if (this.eligibleTotal(cartReady) < threshold) throw new Error('O carrinho não atingiu a meta desse brinde.');
      const result = await this.post(Theme.routes.cart_add_url, {
        items: [{ id: variantId, quantity: 1, properties: { _99k_gift: 'true' } }], ...rendering,
      });
      cartChanged = true;
      const cartAfter = await this.getCart();
      const chosen = cartAfter.items.find((item) => Number(item.variant_id) === variantId && item.properties?._99k_gift === 'true');
      const giftQuantity = cartAfter.items.filter((item) => gifts.has(Number(item.variant_id))).reduce((total, item) => total + item.quantity, 0);
      if (!chosen) throw new Error('Confira o carrinho antes de tentar novamente.');
      if (chosen.final_line_price !== 0 || chosen.quantity !== 1 || giftQuantity !== 1) {
        await this.post(Theme.routes.cart_change_url, { id: chosen.key, quantity: 0, ...rendering });
        throw new Error('A troca não pôde ser concluída com 1 brinde gratuito.');
      }
      button.textContent = 'Selecionado';
      this.publishCart(cartAfter, result.sections);
    } catch (error) {
      let errorText = error.message || 'Não foi possível atualizar o brinde.';
      if (removedPrevious && previousGift) {
        try {
          const currentCart = await this.getCart();
          if (!currentCart.items.some((item) => gifts.has(Number(item.variant_id))) && this.eligibleTotal(currentCart) >= gifts.get(Number(previousGift.variant_id))) {
            await this.post(Theme.routes.cart_add_url, {
              items: [{ id: previousGift.variant_id, quantity: previousGift.quantity, properties: previousGift.properties || {} }], ...rendering,
            });
            const restoredCart = await this.getCart();
            const restoredGift = restoredCart.items.find((item) => Number(item.variant_id) === Number(previousGift.variant_id));
            if (restoredGift?.quantity === 1 && restoredGift.final_line_price === 0) {
              errorText += ' O brinde anterior foi recuperado.';
            } else {
              errorText += ' Confira o valor do brinde no carrinho antes de finalizar.';
            }
          } else {
            errorText += ' Confira qual brinde ficou no carrinho.';
          }
        } catch {
          errorText += ' Não foi possível recuperar o brinde anterior. Confira o carrinho.';
        }
      }
      if (cartChanged) {
        try { await this.refresh(sections); } catch { errorText += ' Recarregue a página.'; }
        if (message) message.textContent = errorText;
      } else {
        originalStates.forEach(({ button: item, disabled, text }) => { item.disabled = disabled; item.textContent = text; });
        if (message) message.textContent = errorText;
      }
    } finally {
      GiftSelector99K.busy = false;
    }
  };
}

// Keep the selected promotional gift only while its own minimum spend is met.
// This listener is shared by the drawer and cart page; it is registered once per module.
let giftEligibilityTimer99K;
let giftCleanupRunning99K = false;

function scheduleGiftEligibility99K() {
  clearTimeout(giftEligibilityTimer99K);
  giftEligibilityTimer99K = setTimeout(removeIneligibleGift99K, 350);
}

async function removeIneligibleGift99K() {
  if (giftCleanupRunning99K) return;
  if (GiftSelector99K.busy) {
    scheduleGiftEligibility99K();
    return;
  }
  giftCleanupRunning99K = true;
  let ownsLock = false;
  let selector;
  let changed = false;
  let sections = [];
  try {
    await Promise.all(CartItemsComponent.pendingQuantityUpdates);
    if (GiftSelector99K.busy) {
      scheduleGiftEligibility99K();
      return;
    }
    selector = document.querySelector('gift-switch-99k');
    if (!selector) return;
    GiftSelector99K.busy = true;
    ownsLock = true;
    const sectionIds = new Set([selector.dataset.sectionId, 'cart-drawer-section']);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => sectionIds.add(item.dataset.sectionId));
    sections = Array.from(sectionIds).filter(Boolean).slice(0, 5);
    let cart = await selector.getCart();
    let lastSections;
    // At most three gift types can be removed in a single pass.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const total = selector.eligibleTotal(cart);
      const invalidGift = cart.items.find((item) =>
        gifts.has(Number(item.variant_id)) &&
        item.properties?._99k_gift === 'true' &&
        total < gifts.get(Number(item.variant_id))
      );
      if (!invalidGift) break;
      const result = await selector.post(Theme.routes.cart_change_url, {
        id: invalidGift.key, quantity: 0, sections, sections_url: window.location.pathname,
      });
      changed = true;
      lastSections = result.sections;
      cart = await selector.getCart();
    }
    if (changed) {
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'remove', context: 'cart', lines: [],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { sections: lastSections, items: cart.items, itemCount: cart.item_count, source: '99k-gift-auto-remove', didError: false },
        }),
      }));
    }
  } catch (error) {
    console.warn('[99K] Não foi possível conferir/remover o brinde:', error);
    if (changed && selector) {
      try { await selector.refresh(sections); } catch { /* Keep the current page; the shopper can retry. */ }
    }
    document.querySelectorAll('[data-gift-message]').forEach((message) => {
      message.textContent = 'Não foi possível conferir a permanência do brinde. Recarregue o carrinho antes de finalizar.';
    });
  } finally {
    if (ownsLock) GiftSelector99K.busy = false;
    giftCleanupRunning99K = false;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.promise) {
    Promise.resolve(event.promise).then(scheduleGiftEligibility99K).catch(() => {});
  } else {
    scheduleGiftEligibility99K();
  }
});
document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => {
  if (event.promise) Promise.resolve(event.promise).then(scheduleGiftEligibility99K).catch(() => {});
  else scheduleGiftEligibility99K();
});
window.addEventListener('pageshow', scheduleGiftEligibility99K);

if (!customElements.get('gift-switch-99k')) customElements.define('gift-switch-99k', GiftSelector99K);
